import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  LinearProgress,
  Snackbar,
  Alert
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { mockCourses, mockEnrollments } from '../data/coursesData';
import EnrollCourseModal from '../components/courses/EnrollCourseModal';
import { Course } from '../types';

const Training: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const getLevelColor = (level: string) => {
    switch (level) {
      case 'Básico':
        return 'success';
      case 'Intermedio':
        return 'warning';
      case 'Avanzado':
        return 'error';
      default:
        return 'default';
    }
  };

  // Obtener progreso del usuario (simulado)
  const getUserProgress = (courseId: string) => {
    const enrollment = mockEnrollments.find(e => e.courseId === courseId);
    return enrollment?.progress || 0;
  };

  const handleEnrollCourse = (enrollment: any) => {
    console.log('Nueva inscripción:', enrollment);
    setSnackbar({ 
      open: true, 
      message: `¡Te has inscrito exitosamente en "${selectedCourse?.title}"!` 
    });
  };

  const handleOpenModal = (course: Course) => {
    setSelectedCourse(course);
    setOpenModal(true);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Catálogo de Cursos
      </Typography>

      {/* Estadísticas */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 4 }}>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Cursos Disponibles
            </Typography>
            <Typography variant="h4">{mockCourses.length}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Mis Inscripciones
            </Typography>
            <Typography variant="h4">{mockEnrollments.length}</Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Cursos Completados
            </Typography>
            <Typography variant="h4">
              {mockEnrollments.filter(e => e.progress === 100).length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Catálogo de cursos */}
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Todos los Cursos
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 3 }}>
        {mockCourses.map(course => {
          const progress = getUserProgress(course.id);
          const isEnrolled = progress > 0;

          return (
            <Card key={course.id} sx={{ display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Chip 
                    label={course.level} 
                    size="small" 
                    color={getLevelColor(course.level) as any}
                  />
                  <Chip label={course.category} size="small" variant="outlined" />
                </Box>
                
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {course.title}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {course.description}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AccessTimeIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary">
                    {course.duration} minutos • {course.contents.length} módulos
                  </Typography>
                </Box>

                {isEnrolled && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Progreso
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {progress}%
                      </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={progress} />
                  </Box>
                )}
              </CardContent>
              
              <CardActions>
                <Button
                  fullWidth
                  variant={isEnrolled ? 'contained' : 'outlined'}
                  startIcon={isEnrolled ? <PlayCircleOutlineIcon /> : undefined}
                  onClick={() => isEnrolled ? alert('Continuar curso') : handleOpenModal(course)}
                >
                  {isEnrolled ? 'Continuar' : 'Inscribirse'}
                </Button>
              </CardActions>
            </Card>
          );
        })}
      </Box>

      <EnrollCourseModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSubmit={handleEnrollCourse}
        course={selectedCourse}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Training;
